import React, { useCallback, useMemo, useRef, useState } from 'react';
import { sitesApi } from './sitesApi';

const btnStyle = {
  padding: '0.4rem 0.75rem',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  background: '#fff',
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontWeight: 600,
};

const primaryBtn = {
  ...btnStyle,
  background: '#171A0D',
  color: '#fff',
  borderColor: '#171A0D',
};

function emptyMedia() {
  return { items: [], slots: {}, logo: null };
}

/** Match `packages/site-generate` `slugify(s.name)` — render slot ids ignore brief `services[].slug`. */
function serviceSlugFromBrief(s) {
  return String(s?.name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function mediaSlotsForBrief(brief) {
  const services = (brief?.services || [])
    .map((s) => ({
      name: s?.name,
      slug: serviceSlugFromBrief(s),
    }))
    .filter((s) => s.slug);
  const slots = [{ id: 'hero', label: 'Hero', aspect: '16:9' }];
  for (const s of services) {
    slots.push({
      id: `service-${s.slug}`,
      label: s.name || String(s.slug).replace(/-/g, ' '),
      aspect: '4:3',
    });
  }
  const tail = [
    { id: 'feature-1', label: 'Feature 1', aspect: '4:3' },
    { id: 'feature-2', label: 'Feature 2', aspect: '4:3' },
    { id: 'feature-3', label: 'Feature 3', aspect: '4:3' },
    { id: 'gallery-1', label: 'Gallery 1', aspect: '4:3' },
    { id: 'gallery-2', label: 'Gallery 2', aspect: '4:3' },
    { id: 'gallery-3', label: 'Gallery 3', aspect: '4:3' },
    { id: 'gallery-4', label: 'Gallery 4', aspect: '4:3' },
    { id: 'faq', label: 'FAQ', aspect: '3:4' },
    { id: 'cta', label: 'CTA band', aspect: '16:9' },
  ];
  return slots.concat(tail);
}

function previewAssetBase(previewUrl) {
  if (!previewUrl) return '';
  try {
    const u = new URL(previewUrl);
    const dir = u.pathname.replace(/\/[^/]*$/, '/');
    return `${u.origin}${dir}`;
  } catch {
    return '';
  }
}

function slotPreviewThumb(previewUrl, slotId) {
  const base = previewAssetBase(previewUrl);
  return base ? `${base}assets/media/${slotId}-480.jpg` : null;
}

function logoPreviewThumb(previewUrl) {
  const base = previewAssetBase(previewUrl);
  return base ? `${base}assets/media/logo.png` : null;
}

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      resolve(raw.includes(',') ? raw.split(',')[1] : raw);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function MediaPanel({
  workspaceId,
  siteId,
  brief,
  photos,
  currentLook,
  onBriefChange,
  onPreviewUrl,
  previewUrl,
}) {
  const media = brief?.media && Array.isArray(brief.media.items) ? brief.media : emptyMedia();
  const slots = useMemo(() => mediaSlotsForBrief(brief), [brief]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [genNotes, setGenNotes] = useState({});
  const [chooseSlot, setChooseSlot] = useState(null);
  const [fillProgress, setFillProgress] = useState(null);
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const look = currentLook || brief?.look || 'bold-block';
  const renderChainRef = useRef(Promise.resolve());

  const refreshPreview = useCallback(async () => {
    if (!workspaceId || !siteId) return;
    renderChainRef.current = renderChainRef.current.then(async () => {
      const res = await sitesApi.renderSite(workspaceId, siteId, { look });
      if (res?.previewUrl && onPreviewUrl) {
        const sep = res.previewUrl.includes('?') ? '&' : '?';
        onPreviewUrl(`${res.previewUrl}${sep}_=${Date.now()}`);
      }
    });
    await renderChainRef.current;
  }, [workspaceId, siteId, look, onPreviewUrl]);

  const applyMedia = useCallback(
    async (nextMedia) => {
      onBriefChange({ ...brief, media: nextMedia });
      setError('');
      await refreshPreview();
    },
    [brief, onBriefChange, refreshPreview]
  );

  const runAction = useCallback(
    async (action, extra = {}) => {
      if (!workspaceId || !siteId) return null;
      setBusy(action);
      setError('');
      try {
        const res = await sitesApi.mediaAction(workspaceId, siteId, action, extra);
        if (res?.media) await applyMedia(res.media);
        return res;
      } catch (e) {
        setError(e.message || 'Something went wrong.');
        return null;
      } finally {
        setBusy('');
      }
    },
    [workspaceId, siteId, applyMedia]
  );

  const catalog = (photos || []).filter((p) => p?.url && p.kind !== 'people');

  const emptySlotCount = slots.filter((s) => !media.slots[s.id]).length;
  const fillPrice = (emptySlotCount * 0.04).toFixed(2);
  const fillLabel = `Fill ${emptySlotCount} empty slots with generated images (${emptySlotCount} slots, $${fillPrice})`;

  const showEmulatorFileInputs = process.env.REACT_APP_USE_EMULATORS === 'true';
  const fileInputStyle = showEmulatorFileInputs
    ? { width: '100%', marginBottom: '0.5rem', fontSize: '0.8rem' }
    : { position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' };

  const logoItem = media.logo ? media.items.find((i) => i.id === media.logo) : null;
  const logoSrc = logoPreviewThumb(previewUrl);

  async function handleCopy(url) {
    await runAction('copy', { urls: [url] });
  }

  async function handleUploadPhoto(file) {
    if (!file) return;
    const dataBase64 = await readFileBase64(file);
    await runAction('upload', {
      filename: file.name,
      contentType: file.type || 'image/png',
      dataBase64,
      kind: 'photo',
    });
  }

  async function handleUploadLogo(file) {
    if (!file) return;
    const dataBase64 = await readFileBase64(file);
    await runAction('upload', {
      filename: file.name,
      contentType: file.type || 'image/png',
      dataBase64,
      kind: 'logo',
    });
  }

  async function handleSetLogoFromCatalog(url) {
    const res = await runAction('copy', { urls: [url] });
    if (res?.media) {
      const logo = res.media.items.find((i) => i.kind === 'logo' && i.originUrl === url);
      if (logo) await runAction('setLogo', { mediaId: logo.id });
    }
  }

  async function handleGenerate(slotId) {
    const note = genNotes[slotId] || '';
    await runAction('generate', { slot: slotId, ...(note ? { note } : {}) });
  }

  // One request per slot: a generation takes 30-60 s (Gemini + encodes), so a single 12-slot request ran past the
  // function timeout and the browser saw "Failed to fetch" while the images had already been paid for.
  async function handleFill() {
    if (!workspaceId || !siteId) return;
    const empty = slots.filter((s) => !media.slots[s.id]);
    if (!empty.length) return;
    setBusy('fill');
    setError('');
    let current = media;
    const failures = [];
    try {
      for (let i = 0; i < empty.length; i++) {
        const s = empty[i];
        setFillProgress({ done: i, total: empty.length, label: s.label || s.id });
        let attempt = 0;
        while (attempt < 2) {
          try {
            const res = await sitesApi.mediaAction(workspaceId, siteId, 'generate', { slot: s.id });
            if (res?.media) {
              current = res.media;
              onBriefChange({ ...brief, media: current });
            }
            break;
          } catch (e) {
            attempt += 1;
            if (attempt >= 2 || !/few seconds|rate/i.test(e.message || '')) {
              failures.push(`${s.label || s.id}: ${e.message || 'failed'}`);
              break;
            }
            await new Promise((r) => setTimeout(r, 6000));
          }
        }
      }
      if (failures.length) setError(`Could not generate ${failures.length} of ${empty.length}. ${failures.join(' / ')}`);
      await refreshPreview();
    } finally {
      setBusy('');
      setFillProgress(null);
    }
  }

  async function handleAssign(slotId, mediaId) {
    await runAction('assign', { slots: { [slotId]: mediaId } });
    setChooseSlot(null);
  }

  async function handleClearSlot(slotId) {
    await runAction('assign', { slots: { [slotId]: null } });
  }

  async function handleRemoveItem(mediaId) {
    await runAction('remove', { mediaId });
  }

  async function handleAltBlur(mediaId, alt) {
    await runAction('setAlt', { mediaId, alt });
  }

  async function handleClearLogo() {
    await runAction('setLogo', { mediaId: null });
  }

  return (
    <div style={{ padding: '0 0.25rem' }}>
      {error ? (
        <p style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }} role="alert">
          {error}
        </p>
      ) : null}

      <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>From your current site</h3>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
        We copy and optimize the photos you pick; the rest stay on your old site.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {catalog.map((ph) => (
          <div
            key={ph.url}
            style={{
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'center',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '0.5rem',
            }}
          >
            <img
              src={ph.url}
              alt=""
              loading="lazy"
              style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 4, background: '#f3f4f6' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'capitalize' }}>{ph.kind || 'photo'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
                <button type="button" style={btnStyle} disabled={!!busy} onClick={() => handleCopy(ph.url)}>
                  Use
                </button>
                {(String(ph.kind || '').toLowerCase() === 'logo') && (
                  <button type="button" style={btnStyle} disabled={!!busy} onClick={() => handleSetLogoFromCatalog(ph.url)}>
                    Use as logo
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!catalog.length && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>No photos cataloged yet.</p>}
      </div>

      <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Upload</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          style={fileInputStyle}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUploadPhoto(f);
            e.target.value = '';
          }}
        />
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          style={fileInputStyle}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUploadLogo(f);
            e.target.value = '';
          }}
        />
        <button type="button" style={btnStyle} disabled={!!busy} onClick={() => photoInputRef.current?.click()}>
          Upload photo
        </button>
        <button type="button" style={btnStyle} disabled={!!busy} onClick={() => logoInputRef.current?.click()}>
          Upload logo
        </button>
      </div>

      <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Logo</h3>
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div
            style={{
              flex: 1,
              padding: '1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              minHeight: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {logoItem && logoSrc ? (
              <img src={logoSrc} alt="" style={{ maxHeight: 48, maxWidth: '100%', width: 'auto', objectFit: 'contain' }} />
            ) : logoItem ? (
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Logo set (refresh preview to preview)</span>
            ) : (
              <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>No logo yet</span>
            )}
          </div>
          <div
            style={{
              flex: 1,
              padding: '1rem',
              background: '#171A0D',
              border: '1px solid #374151',
              borderRadius: 8,
              minHeight: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {logoItem && logoSrc ? (
              <img src={logoSrc} alt="" style={{ maxHeight: 48, maxWidth: '100%', width: 'auto', objectFit: 'contain' }} />
            ) : logoItem ? (
              <span style={{ fontSize: '0.85rem', color: '#e5e7eb' }}>Logo set</span>
            ) : (
              <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>No logo yet</span>
            )}
          </div>
        </div>
        {logoItem ? (
          <button type="button" style={btnStyle} disabled={!!busy} onClick={handleClearLogo}>
            Remove logo
          </button>
        ) : null}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '1rem', margin: 0 }}>Photo slots</h3>
        {emptySlotCount > 0 ? (
          <button type="button" style={primaryBtn} disabled={!!busy || emptySlotCount === 0} onClick={handleFill}>
            {fillProgress ? `Generating ${fillProgress.done + 1} of ${fillProgress.total}: ${fillProgress.label}` : fillLabel}
          </button>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
        {slots.map((slot) => {
          const assignedId = media.slots[slot.id];
          const item = assignedId ? media.items.find((i) => i.id === assignedId) : null;
          const thumbSrc = assignedId
            ? slotPreviewThumb(previewUrl, slot.id) || item?.originUrl || null
            : null;
          return (
            <div
              key={slot.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '0.65rem 0.75rem',
                background: '#fafafa',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                <div>
                  <strong style={{ fontSize: '0.9rem' }}>{slot.label}</strong>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.35rem' }}>{slot.aspect}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', justifyContent: 'flex-end' }}>
                  <button type="button" style={btnStyle} disabled={!!busy} onClick={() => setChooseSlot(slot.id)}>
                    Choose
                  </button>
                  <button type="button" style={btnStyle} disabled={!!busy} onClick={() => handleGenerate(slot.id)}>
                    Generate
                  </button>
                  {assignedId ? (
                    <button type="button" style={btnStyle} disabled={!!busy} onClick={() => handleClearSlot(slot.id)}>
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
              {item ? (
                <div style={{ marginTop: '0.5rem' }}>
                  {thumbSrc ? (
                    <img
                      src={thumbSrc}
                      alt=""
                      style={{
                        width: '100%',
                        maxWidth: 200,
                        aspectRatio: slot.aspect.replace(':', ' / '),
                        objectFit: 'cover',
                        borderRadius: 6,
                        background: '#e5e7eb',
                        marginBottom: '0.35rem',
                      }}
                    />
                  ) : null}
                  {item.source === 'generated' ? (
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>AI generated</span>
                  ) : null}
                  <input
                    type="text"
                    name={`alt-${item.id}`}
                    placeholder="Alt text"
                    defaultValue={item.alt || ''}
                    key={`${item.id}-${item.alt}`}
                    onBlur={(e) => handleAltBlur(item.id, e.target.value)}
                    style={{
                      width: '100%',
                      marginTop: '0.35rem',
                      padding: '0.4rem 0.5rem',
                      borderRadius: 6,
                      border: '1px solid #d1d5db',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ) : (
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>Empty</p>
              )}
              <input
                type="text"
                placeholder="Optional style note for Generate"
                value={genNotes[slot.id] || ''}
                onChange={(e) => setGenNotes((prev) => ({ ...prev, [slot.id]: e.target.value }))}
                style={{
                  width: '100%',
                  marginTop: '0.35rem',
                  padding: '0.35rem 0.5rem',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  fontSize: '0.8rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          );
        })}
      </div>

      {chooseSlot ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => setChooseSlot(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: '1rem', maxWidth: 360, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ marginTop: 0 }}>Choose a photo</h4>
            {media.items.filter((i) => i.kind === 'photo').map((i) => (
              <button
                key={i.id}
                type="button"
                style={{ ...btnStyle, display: 'block', width: '100%', marginBottom: '0.35rem', textAlign: 'left' }}
                onClick={() => handleAssign(chooseSlot, i.id)}
              >
                {i.alt || i.id.slice(0, 8)}
                {i.source === 'generated' ? ' (AI generated)' : ''}
              </button>
            ))}
            {!media.items.filter((i) => i.kind === 'photo').length && (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Upload or copy a photo first.</p>
            )}
          </div>
        </div>
      ) : null}

      {media.items.some((i) => i.source === 'generated') ? (
        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>AI generated images are for scene slots only, not people.</p>
      ) : null}
    </div>
  );
}
